import { Component, computed, effect, ElementRef, inject, OnInit, Signal, signal, viewChild, viewChildren, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonButton, IonIcon, IonContent, IonSearchbar, IonModal, IonLabel } from '@ionic/angular/standalone';
import { ChatModalComponent } from 'src/app/components/chat-modal/chat-modal.component';
import { ConfirmModalComponent } from 'src/app/components/confirm-modal/confirm-modal.component';
import { addIcons } from "ionicons";
import { ellipsisVertical, chevronDownOutline, trashOutline, createOutline } from "ionicons/icons";
import { GlobalService } from 'src/app/services/global.service';
import { Chat } from "src/app/models/chat";
import { IonInput } from '@ionic/angular/standalone';
import { AuthService } from 'src/app/services/auth.service';
import { TmpDashboardComponent } from 'src/app/components/tmp-dashboard/tmp-dashboard.component';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
  standalone: true,
  imports: [IonLabel, IonModal, IonContent, IonButton, IonIcon, CommonModule, FormsModule, ChatModalComponent, IonInput, ConfirmModalComponent, ReactiveFormsModule, IonSearchbar, TmpDashboardComponent]
})
export class ChatPage implements OnInit {
  public scrollContainer: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('scrollContainer');
  
  // Set modals state
  public isEdit: boolean;
  public editModal: WritableSignal<boolean> = signal(false);
  public confirmModal: WritableSignal<boolean> = signal(false); 
  public expeModal: WritableSignal<boolean> = signal(false);

  // Set active chat accent color
  public accentColor: WritableSignal<string> = signal<string>('');
  
  // Chat list
  public chats: Signal<Chat[]> = computed<Chat[]>(() => this.globalService.chatArray());
  public chatAnim: WritableSignal<boolean> = signal(true);

  // Data passed to chat-modal
  public context: WritableSignal<string | null> = signal(null);
  public currentChatId: WritableSignal<number> = signal<number>(-1);

  // Delete chat
  private removedChatIndex: number;
  public removedChatTitle: string;

  // Edit chat
  public editChatId: number;
  public setChatTitle: FormControl<string | null> = new FormControl<string>("Nouveau chat");

  // Elmnt to search with the searchbar
  public chatElements: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren<ElementRef<HTMLElement>>('chatItem');

  // Chat list state for mobile
  public isChatListOpen: boolean = false;
  public hideChatList: boolean = true;
  
  constructor(public globalService: GlobalService, public authService: AuthService) {
    addIcons({ ellipsisVertical, chevronDownOutline, trashOutline, createOutline });
  }

  async ngOnInit() {
    await this.authService.updateUserConnectionStatus();    
    if (this.authService.currentUser().isConnected) {
      await this.globalService.getAllChat(this.authService.currentUser().auth.id);    
      if (this.chats().length > 0) {
        const chatId = this.chats()[this.chats().length - 1].id;
        // this.currentChatId.set(chatId);         
      } else {
        this.setDisplayedChat(0, 0, null)
        if (this.globalService.isExpe) this.expeModal.set(true)
      }     
    }
  }

  openChatList(): void {
    this.isChatListOpen = !this.isChatListOpen;
    if (!this.isChatListOpen) {
      setTimeout(() => {
        this.hideChatList = !this.hideChatList;
      }, 200)
    }
    else{
      this.hideChatList = !this.hideChatList;
    }
  }

  setDisplayedChat($index: number, chatId: number, chatContext: string | null) {
    this.currentChatId.set(chatId);
    this.setChatColor($index);
    if(chatContext !== null){
      this.context.set(chatContext);
      this.globalService.getChatByContext(this.authService.currentUser().auth.id, chatContext);
    }
    else if(chatContext === null || chatContext === 'init'){
      setTimeout(()=>{
        this.globalService.emptyMsgArray();
      }, 100)
    }
  }

  openConfirmModal($index: number, title: string) {
    this.removedChatIndex = $index; 
    this.removedChatTitle = title;
    this.confirmModal.set(true);
  }

  deleteChat() {
    this.globalService.deleteChat(this.removedChatIndex)
    this.setChatColor();
    this.currentChatId.set(-1);
  }

  openEditModal(isEdit: boolean, $index?: number, chatTitle?: string) {
    this.isEdit = isEdit;
    this.editModal.set(true);
    if ($index !== undefined) { 
      this.editChatId = $index;
      if (chatTitle) this.setChatTitle.setValue(chatTitle);
    }
    else{
      this.setChatTitle.setValue('Nouveau chat')
    }
  }

  onSubmit() {    
    if (this.isEdit) {
      // this.chats()[this.editChatId].title = this.setChatTitle.value!;
    }
    else{
      this.addChat(this.setChatTitle.value!);
    }
    this.editModal.set(false);  
    this.setChatTitle.setValue('');
  }

  addChat(title: string = '') {  
    this.context.set(null);  
    const chatId = this.chats().length > 0 ? this.chats()[0].id + 1 : 0;  
    this.globalService.addChat(this.context(), chatId, title);
    this.setChatColor(0);
    this.currentChatId.set(chatId);
    this.scrollToTop();
  }

  setChatColor($index?: number) {
    if ($index !== undefined) {      
      const reverseIndex = (this.chats().length - 1) - $index;
      const chatIndex = (reverseIndex / 4) % 1;
      if (chatIndex === 0 ) {
        this.accentColor.set('green');      
      }
      else if (chatIndex === 0.25) {
        this.accentColor.set('blue');      
      }
      else if (chatIndex === 0.5) {
        this.accentColor.set('purple');      
      }
      else if (chatIndex === 0.75) {
        this.accentColor.set('yellow');      
      }
    }
    else{
      this.accentColor.set('default');
    }
  }

  searchChat(value: string | null | undefined) {
    this.chatElements().forEach((chat: ElementRef<HTMLElement>) => {      
      if (typeof value === 'string') {      
        if (!chat.nativeElement.textContent!.toUpperCase().includes(value.toUpperCase())) {    
          chat.nativeElement.classList.add('hide');
        }
        else{
          chat.nativeElement.classList.remove('hide');
        }
      }
      else{
        chat.nativeElement.classList.remove('hide');
      }
    });
  }

  scrollToBottom() {
    setTimeout(() => {
      this.scrollContainer().nativeElement.scrollTo({top: this.scrollContainer().nativeElement.scrollHeight, left: 0, behavior: "smooth"});
    }, 0)    
  }

  scrollToTop() {
    setTimeout(() => {
      this.scrollContainer().nativeElement.scrollTo({top: 0, left: 0, behavior: "smooth"});
    }, 0)    
  }

  getChatContext($event: any) { 
    if (this.chats()[0].context == null || this.chats()[0].context == 'init') {      
      this.globalService.chatArray.update((chats: any) =>
        chats.map((c: any, index: any) => index === 0 ? { ...c, context: $event } : c
      ))
    }
    
    if ($event !== 'init') {
      this.context.set($event);    
      this.scrollToTop();      
    }
  }
}